import type { RouteObject } from 'react-router-dom';
import { Navigate, useRoutes } from 'react-router-dom';

import { CustomersPage } from '@/app/pages/CustomersPage';
import { HomePage } from '@/app/pages/HomePage';
import { LandingPage } from '@/app/pages/LandingPage';
import { MediaPage } from '@/app/pages/MediaPage';
import { OrdersPage } from '@/app/pages/OrdersPage';
import { ProductsPage } from '@/app/pages/ProductsPage';
import { StorePage } from '@/app/pages/StorePage';
import { SignUpPage } from '@/app/pages/SignUpPage';
import { TenantSelectPage } from '@/app/pages/TenantSelectPage';
import { contentRoutes } from '@/features/content';
import { catalogRoutes } from '@/features/catalog';
import { EmployeesPage } from '@/features/employees';
import { loyaltyRoutes } from '@/features/loyalty';
import { settingsRoutes } from '@/features/store-settings';
import { AppLayout } from '@/shared/ui/AppLayout';
import { NotFoundPage } from '@/shared/ui/NotFoundPage';

import { RequireAuth } from './RequireAuth';

/**
 * Application route table.
 *
 * Public routes (`/welcome`, `/sign-up`) render standalone. Everything else lives
 * behind {@link RequireAuth} — which gates on the session, routes multi-company users
 * to tenant selection, and renders the {@link AppLayout} shell for protected pages.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const appRoutes: RouteObject[] = [
  // ── Public (unauthenticated) ────────────────────────────────────────────────
  { path: '/welcome', element: <LandingPage /> },
  { path: '/sign-up', element: <SignUpPage /> },

  // ── Authenticated application ─────────────────────────────────────────────────
  {
    element: <RequireAuth />,
    children: [
      { path: '/select-tenant', element: <TenantSelectPage /> },
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <HomePage /> },
          catalogRoutes,
          { path: 'products', element: <ProductsPage /> },
          { path: 'orders', element: <OrdersPage /> },
          { path: 'customers', element: <CustomersPage /> },
          { path: 'media', element: <MediaPage /> },
          { path: 'store', element: <StorePage /> },
          contentRoutes,
          loyaltyRoutes,
          { path: 'team', element: <EmployeesPage /> },
          settingsRoutes,
          { path: 'not-found', element: <NotFoundPage /> },
          // Unknown URLs fall back to the not-found page.
          { path: '*', element: <Navigate to="/not-found" replace /> },
        ],
      },
    ],
  },
];

/** Renders the active route for the current location. */
export function AppRoutes() {
  return useRoutes(appRoutes);
}
